import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitCompare,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  BookOpen,
  Layers,
  Award,
  AlertTriangle,
  CheckCircle,
  Search,
  Download,
  School,
  Sliders,
  Filter,
  XCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useGetResultOverallAllKVsQuery,
  useGetResultYearValueDropdownQuery,
} from '../services/api';
import KVSLoader from '../../../components/loaders/KVSLoader';

// Types for merged record
interface MergedSchoolRecord {
  kvCode: string;
  schoolName: string;
  region: string;
  state: string;
  district: string;
  class10Appeared: number;
  class10Passed: number;
  class10PassPercentage: number;
  class10PI: number;
  class12Appeared: number;
  class12Passed: number;
  class12PassPercentage: number;
  class12PI: number;
}

const TABS = [
  { id: 'class10-100', label: 'Class 10 (100% Pass)', icon: CheckCircle },
  { id: 'class12-100', label: 'Class 12 (100% Pass)', icon: CheckCircle },
  { id: 'both-100', label: 'Both 100%', icon: Award },
  { id: 'neither-100', label: 'Neither 100%', icon: XCircle },
  { id: 'discrepancy', label: 'One 100%, Other Not', icon: AlertTriangle },
  { id: 'pi-filter', label: 'Performance Index (PI) Filter', icon: Sliders },
];

export default function ComparisonAnalysis() {
  const navigate = useNavigate();
  const [selectedYear, setSelectedYear] = useState<number>(2026);
  const [activeTab, setActiveTab] = useState<string>('class10-100');
  const [search, setSearch] = useState<string>('');

  // PI filter state
  const [piMin, setPiMin] = useState<number | ''>('');
  const [piMax, setPiMax] = useState<number | ''>(80);
  const [piClass, setPiClass] = useState<'10' | '12' | 'both'>('both');

  // Sorting state
  const [sorts, setSorts] = useState<Array<{ key: string; dir: 'asc' | 'desc' }>>([]);

  // Helper to retrieve values for sorting
  const getSortValue = (item: MergedSchoolRecord, key: string): number | string => {
    switch (key) {
      case 'schoolName':
        return item.schoolName.toLowerCase();
      case 'region':
        return item.region.toLowerCase();
      case 'class10Appeared':
        return item.class10Appeared;
      case 'class10Passed':
        return item.class10Passed;
      case 'class10PassPercentage':
        return item.class10PassPercentage;
      case 'class10PI':
        return item.class10PI;
      case 'class12Appeared':
        return item.class12Appeared;
      case 'class12Passed':
        return item.class12Passed;
      case 'class12PassPercentage':
        return item.class12PassPercentage;
      case 'class12PI':
        return item.class12PI;
      case 'gap':
        return Math.abs(item.class10PassPercentage - item.class12PassPercentage);
      default:
        return 0;
    }
  };

  const handleSort = (key: string, isShiftPressed: boolean) => {
    setSorts(prev => {
      const existingIdx = prev.findIndex(s => s.key === key);
      if (isShiftPressed) {
        // Multi-column cascading sort
        if (existingIdx === -1) {
          return [...prev, { key, dir: 'asc' }];
        } else if (prev[existingIdx].dir === 'asc') {
          const copy = [...prev];
          copy[existingIdx] = { key, dir: 'desc' };
          return copy;
        } else {
          return prev.filter(s => s.key !== key);
        }
      } else {
        // Single-column sort
        if (existingIdx !== -1 && prev.length === 1) {
          if (prev[0].dir === 'asc') {
            return [{ key, dir: 'desc' }];
          } else {
            return [];
          }
        } else {
          return [{ key, dir: 'asc' }];
        }
      }
    });
  };

  const renderSortableHeader = (label: string, sortKey: string, align: 'left' | 'center' = 'left', extraClasses: string = '') => {
    const sortIdx = sorts.findIndex(s => s.key === sortKey);
    const isActive = sortIdx !== -1;
    const activeSort = isActive ? sorts[sortIdx] : null;

    return (
      <th
        className={`py-3.5 px-4 font-semibold select-none cursor-pointer hover:bg-gray-100/70 transition-colors group ${align === 'center' ? 'text-center' : 'text-left'
          } ${extraClasses}`}
        onClick={(e) => handleSort(sortKey, e.shiftKey)}
        title="Click to sort, Shift+Click to multi-sort"
      >
        <div className={`flex items-center gap-1.5 ${align === 'center' ? 'justify-center' : ''}`}>
          <span>{label}</span>
          <div className="flex items-center gap-0.5">
            {isActive ? (
              activeSort?.dir === 'asc' ? (
                <span className="text-[#F36B21] font-bold text-xs">▲</span>
              ) : (
                <span className="text-[#F36B21] font-bold text-xs">▼</span>
              )
            ) : (
              <span className="text-gray-300 group-hover:text-gray-400 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity">⇅</span>
            )}
            {sorts.length > 1 && isActive && (
              <span className="bg-orange-100 text-[#F36B21] text-[10px] px-1 rounded font-black leading-none">
                {sortIdx + 1}
              </span>
            )}
          </div>
        </div>
      </th>
    );
  };

  // Year dropdown values
  const { data: yearDropdownData } = useGetResultYearValueDropdownQuery({ grade: 10 });
  const yearOptions = useMemo(() => {
    const nums = (yearDropdownData || []).map(Number).filter(Number.isFinite);
    return nums.length > 0 ? nums : [selectedYear];
  }, [yearDropdownData, selectedYear]);

  React.useEffect(() => {
    if (yearOptions.length > 0 && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[yearOptions.length - 1]);
    }
  }, [yearOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Parallel RTK Query fetches for Class 10 and Class 12
  const { data: class10Data, isLoading: class10Loading } = useGetResultOverallAllKVsQuery({ grade: 10, year: selectedYear });
  const { data: class12Data, isLoading: class12Loading } = useGetResultOverallAllKVsQuery({ grade: 12, year: selectedYear });

  const isLoading = class10Loading || class12Loading;

  // Merge Class 10 & 12 details on client side
  const mergedKVs = useMemo<MergedSchoolRecord[]>(() => {
    if (!class10Data?.KVs || !class12Data?.KVs) return [];

    const c12Map = new Map<string, typeof class12Data.KVs[0]>();
    class12Data.KVs.forEach(item => {
      c12Map.set(String(item.kvCode || item.schoolName).toUpperCase(), item);
    });

    const list: MergedSchoolRecord[] = [];
    const class10Keys = new Set<string>();

    class10Data.KVs.forEach(c10 => {
      const key = String(c10.kvCode || c10.schoolName).toUpperCase();
      class10Keys.add(key);
      const c12 = c12Map.get(key);

      list.push({
        kvCode: String(c10.kvCode),
        schoolName: c10.schoolName,
        region: c10.region || '',
        state: c10.state || '',
        district: c10.district || '',
        class10Appeared: c10.totalAppeared || 0,
        class10Passed: c10.totalPassed || 0,
        class10PassPercentage: c10.passPercentage || 0,
        class10PI: c10.performanceIndex || 0,
        class12Appeared: c12?.totalAppeared || 0,
        class12Passed: c12?.totalPassed || 0,
        class12PassPercentage: c12?.passPercentage || 0,
        class12PI: c12?.performanceIndex || 0,
      });
    });

    // Handle any Class 12 schools that aren't in Class 10 list
    class12Data.KVs.forEach(c12 => {
      const key = String(c12.kvCode || c12.schoolName).toUpperCase();
      if (!class10Keys.has(key)) {
        list.push({
          kvCode: String(c12.kvCode),
          schoolName: c12.schoolName,
          region: c12.region || '',
          state: c12.state || '',
          district: c12.district || '',
          class10Appeared: 0, class10Passed: 0, class10PassPercentage: 0, class10PI: 0,
          class12Appeared: c12.totalAppeared || 0,
          class12Passed: c12.totalPassed || 0,
          class12PassPercentage: c12.passPercentage || 0,
          class12PI: c12.performanceIndex || 0,
        });
      }
    });

    return list;
  }, [class10Data, class12Data]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (!mergedKVs.length) return { total: 0, c10_100: 0, c12_100: 0, both_100: 0, neither_100: 0, only_c10: 0, only_c12: 0, discrepancy: 0 };

    const c10_100 = mergedKVs.filter(k => k.class10PassPercentage === 100 && k.class10Appeared > 0).length;
    const c12_100 = mergedKVs.filter(k => k.class12PassPercentage === 100 && k.class12Appeared > 0).length;
    const both_100 = mergedKVs.filter(k => k.class10PassPercentage === 100 && k.class10Appeared > 0 && k.class12PassPercentage === 100 && k.class12Appeared > 0).length;
    // Schools with data in both classes but neither is 100%
    const neither_100 = mergedKVs.filter(k => k.class10Appeared > 0 && k.class12Appeared > 0 && k.class10PassPercentage < 100 && k.class12PassPercentage < 100).length;
    // Schools with Class 10 data only (no Class 12 appeared)
    const only_c10 = mergedKVs.filter(k => k.class10Appeared > 0 && k.class12Appeared === 0).length;
    const only_c12 = mergedKVs.filter(k => k.class12Appeared > 0 && k.class10Appeared === 0).length;
    const discrepancy = mergedKVs.filter(
      k =>
        (k.class10PassPercentage === 100 && k.class10Appeared > 0 && k.class12PassPercentage < 100 && k.class12Appeared > 0) ||
        (k.class12PassPercentage === 100 && k.class12Appeared > 0 && k.class10PassPercentage < 100 && k.class10Appeared > 0)
    ).length;

    return { total: mergedKVs.length, c10_100, c12_100, both_100, neither_100, only_c10, only_c12, discrepancy };
  }, [mergedKVs]);

  // ── Regional Bar Chart (ALL regions, not just top 10) ───────────────────
  const regionalChartData = useMemo(() => {
    const map = new Map<string, { c10Sum: number; c10Count: number; c12Sum: number; c12Count: number }>();

    mergedKVs.forEach(item => {
      const reg = item.region || 'Unknown';
      if (!map.has(reg)) map.set(reg, { c10Sum: 0, c10Count: 0, c12Sum: 0, c12Count: 0 });
      const d = map.get(reg)!;
      if (item.class10Appeared > 0) { d.c10Sum += item.class10PI; d.c10Count++; }
      if (item.class12Appeared > 0) { d.c12Sum += item.class12PI; d.c12Count++; }
    });

    return Array.from(map.entries())
      .map(([name, d]) => ({
        region: name,
        'Class 10 PI': d.c10Count > 0 ? +(d.c10Sum / d.c10Count).toFixed(2) : 0,
        'Class 12 PI': d.c12Count > 0 ? +(d.c12Sum / d.c12Count).toFixed(2) : 0,
      }))
      .filter(item => item.region !== 'Unknown')
      .sort((a, b) => b['Class 10 PI'] - a['Class 10 PI']);
  }, [mergedKVs]);

  // ── Filter school records ─────────────────────────────────────────────────
  const filteredBaseData = useMemo(() => {
    const query = search.toLowerCase().trim();
    let base = mergedKVs;

    if (query) {
      base = base.filter(k =>
        k.schoolName.toLowerCase().includes(query) ||
        k.region.toLowerCase().includes(query) ||
        k.state.toLowerCase().includes(query) ||
        k.district.toLowerCase().includes(query) ||
        k.kvCode.toLowerCase().includes(query)
      );
    }

    switch (activeTab) {
      case 'class10-100':
        return base.filter(k => k.class10PassPercentage === 100 && k.class10Appeared > 0);
      case 'class12-100':
        return base.filter(k => k.class12PassPercentage === 100 && k.class12Appeared > 0);
      case 'both-100':
        return base.filter(k => k.class10PassPercentage === 100 && k.class10Appeared > 0 && k.class12PassPercentage === 100 && k.class12Appeared > 0);
      case 'neither-100':
        return base.filter(k => k.class10Appeared > 0 && k.class12Appeared > 0 && k.class10PassPercentage < 100 && k.class12PassPercentage < 100);
      case 'discrepancy':
        return base.filter(k =>
          (k.class10PassPercentage === 100 && k.class10Appeared > 0 && k.class12PassPercentage < 100 && k.class12Appeared > 0) ||
          (k.class12PassPercentage === 100 && k.class12Appeared > 0 && k.class10PassPercentage < 100 && k.class10Appeared > 0)
        );
      case 'pi-filter': {
        const minVal = piMin !== '' ? piMin : 0;
        const maxVal = piMax !== '' ? piMax : 100;
        return base.filter(k => {
          const c10ok = k.class10Appeared > 0 && k.class10PI >= minVal && k.class10PI <= maxVal;
          const c12ok = k.class12Appeared > 0 && k.class12PI >= minVal && k.class12PI <= maxVal;
          if (piClass === '10') return c10ok;
          if (piClass === '12') return c12ok;
          return c10ok || c12ok; // 'both' — either class in range
        });
      }
      default:
        return base;
    }
  }, [mergedKVs, activeTab, search, piMin, piMax, piClass]);

  const filteredDisplayData = useMemo(() => {
    let base = [...filteredBaseData];
    if (sorts.length > 0) {
      base.sort((a, b) => {
        for (const { key, dir } of sorts) {
          const valA = getSortValue(a, key);
          const valB = getSortValue(b, key);
          if (valA !== valB) {
            if (typeof valA === 'number' && typeof valB === 'number') {
              return dir === 'asc' ? valA - valB : valB - valA;
            } else {
              return dir === 'asc'
                ? String(valA).localeCompare(String(valB))
                : String(valB).localeCompare(String(valA));
            }
          }
        }
        return 0;
      });
      return base;
    }

    // Default sorting fallback when no manual sort is active
    switch (activeTab) {
      case 'class10-100':
        return base.sort((a, b) => b.class10Appeared - a.class10Appeared);
      case 'class12-100':
        return base.sort((a, b) => b.class12Appeared - a.class12Appeared);
      case 'both-100':
        return base.sort((a, b) => b.class10PI - a.class10PI);
      case 'neither-100':
        return base.sort((a, b) => (a.class10PassPercentage + a.class12PassPercentage) - (b.class10PassPercentage + b.class12PassPercentage));
      case 'discrepancy':
        return base.sort((a, b) => {
          const devA = Math.abs(a.class10PassPercentage - a.class12PassPercentage);
          const devB = Math.abs(b.class10PassPercentage - b.class12PassPercentage);
          return devB - devA;
        });
      case 'pi-filter':
        return base.sort((a, b) => {
          const getMin = (k: typeof a) => {
            const vals: number[] = [];
            if (k.class10Appeared > 0) vals.push(k.class10PI);
            if (k.class12Appeared > 0) vals.push(k.class12PI);
            return vals.length ? Math.min(...vals) : 999;
          };
          return getMin(a) - getMin(b);
        });
      default:
        return base;
    }
  }, [filteredBaseData, sorts, activeTab]);

  // ── CSV Export ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    let headers: string[] = [];
    let rows: any[][] = [];

    if (activeTab === 'class10-100') {
      headers = ['KV Code', 'School Name', 'Region', 'State', '10th Appeared', '10th Passed', '10th PI'];
      rows = filteredDisplayData.map(k => [k.kvCode, k.schoolName, k.region, k.state, k.class10Appeared, k.class10Passed, k.class10PI]);
    } else if (activeTab === 'class12-100') {
      headers = ['KV Code', 'School Name', 'Region', 'State', '12th Appeared', '12th Passed', '12th PI'];
      rows = filteredDisplayData.map(k => [k.kvCode, k.schoolName, k.region, k.state, k.class12Appeared, k.class12Passed, k.class12PI]);
    } else if (activeTab === 'both-100' || activeTab === 'neither-100') {
      headers = ['KV Code', 'School Name', 'Region', '10th Appeared', '10th Pass%', '10th PI', '12th Appeared', '12th Pass%', '12th PI'];
      rows = filteredDisplayData.map(k => [k.kvCode, k.schoolName, k.region, k.class10Appeared, k.class10PassPercentage.toFixed(1) + '%', k.class10PI.toFixed(2), k.class12Appeared, k.class12PassPercentage.toFixed(1) + '%', k.class12PI.toFixed(2)]);
    } else if (activeTab === 'discrepancy') {
      headers = ['KV Code', 'School Name', 'Region', '10th Appeared', '10th Pass%', '12th Appeared', '12th Pass%', 'Driver'];
      rows = filteredDisplayData.map(k => {
        const driver = k.class10PassPercentage === 100 ? 'Class 10 (100%)' : 'Class 12 (100%)';
        return [k.kvCode, k.schoolName, k.region, k.class10Appeared, k.class10PassPercentage.toFixed(1) + '%', k.class12Appeared, k.class12PassPercentage.toFixed(1) + '%', driver];
      });
    } else {
      headers = ['KV Code', 'School Name', 'Region', '10th PI', '12th PI', 'Status'];
      rows = filteredDisplayData.map(k => {
        const c10Below = k.class10Appeared > 0 && k.class10PI >= piMinVal && k.class10PI <= piMaxVal;
        const c12Below = k.class12Appeared > 0 && k.class12PI >= piMinVal && k.class12PI <= piMaxVal;
        const status = c10Below && c12Below ? 'Both Below' : c10Below ? '10th Only' : '12th Only';
        return [k.kvCode, k.schoolName, k.region, k.class10Appeared > 0 ? k.class10PI : '—', k.class12Appeared > 0 ? k.class12PI : '—', status];
      });
    }

    const csvContent = 'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `KVS_Comparison_${activeTab}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Helpers for table columns ────────────────────────────────────────────
  const isBothTab = activeTab === 'both-100' || activeTab === 'neither-100';
  const piMinVal = piMin !== '' ? piMin : 0;
  const piMaxVal = piMax !== '' ? piMax : 100;
  const chartMinWidth = Math.max(640, regionalChartData.length * 60 + 80);

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/result/classification')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#F36B21] font-medium transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Classifications
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <div>
            <h1 className="text-2xl font-bold text-[#F36B21] flex items-center gap-2">
              <GitCompare className="w-7 h-7" />
              Class 10 &amp; 12 Comparison
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              Side-by-side performance deep dive, discrepancy identification, and custom manual PI analysis
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5">
          <button onClick={() => navigate('/result/classification')} className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors">
            <Layers className="w-3.5 h-3.5" />Classification Analysis<ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => navigate('/result/subject-analysis')} className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors">
            <BookOpen className="w-3.5 h-3.5" />Subject-wise Analysis<ChevronRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => navigate('/result/yearwise-analysis')} className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#F36B21] text-[#F36B21] text-sm font-semibold bg-white hover:bg-orange-50 transition-colors">
            <TrendingUp className="w-3.5 h-3.5" />Year-wise Analysis<ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* ── Filter Panel ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <label className="text-sm font-bold text-gray-500 uppercase tracking-wide">Year</label>
          <div className="relative">
            <select
              value={selectedYear}
              onChange={e => setSelectedYear(Number(e.target.value))}
              className="appearance-none pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21] cursor-pointer"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y - 1}-{String(y).slice(-2)}</option>
              ))}
            </select>
            <ChevronRight className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90" />
          </div>
        </div>
        <div className="text-sm text-gray-400 font-medium">
          Board Results: {selectedYear - 1}-{String(selectedYear).slice(-2)}
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-24 bg-white rounded-2xl border border-gray-200">
          <KVSLoader size={72} color="#F36B21" />
          <p className="text-sm text-gray-400 mt-4">Merging Class 10 &amp; 12 Board performance data...</p>
        </div>
      )}

      {!isLoading && (
        <>
          {/* ── KPI Summary Cards ─────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">

            {/* Total */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-purple-50 text-purple-600 rounded-xl"><School className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">Total KVs</p>
                <p className="text-2xl font-black text-gray-800 mt-0.5">{stats.total.toLocaleString()}</p>
                <p className="text-sm text-gray-400 mt-0.5">All compared schools</p>
              </div>
            </div>

            {/* Class 10 Perfect */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-green-50 text-green-600 rounded-xl"><Award className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">Class 10 Perfect</p>
                <p className="text-2xl font-black text-green-600 mt-0.5">{stats.c10_100.toLocaleString()}</p>
                <p className="text-sm text-gray-400 mt-0.5">100% Pass &bull; Only 10th: <span className="font-bold text-gray-600">{stats.only_c10}</span></p>
              </div>
            </div>

            {/* Class 12 Perfect */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-blue-50 text-blue-600 rounded-xl"><Award className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">Class 12 Perfect</p>
                <p className="text-2xl font-black text-blue-600 mt-0.5">{stats.c12_100.toLocaleString()}</p>
                <p className="text-sm text-gray-400 mt-0.5">100% Pass</p>
              </div>
            </div>

            {/* Both Perfect */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-emerald-50 text-emerald-600 rounded-xl"><Award className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">Both Perfect</p>
                <p className="text-2xl font-black text-emerald-600 mt-0.5">{stats.both_100.toLocaleString()}</p>
                <p className="text-sm text-gray-400 mt-0.5">100% in Both Grades</p>
              </div>
            </div>

            {/* Neither 100% */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-red-50 text-red-600 rounded-xl"><XCircle className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">Neither 100%</p>
                <p className="text-2xl font-black text-red-600 mt-0.5">{stats.neither_100.toLocaleString()}</p>
                <p className="text-sm text-gray-400 mt-0.5">Both classes &lt;100% pass</p>
              </div>
            </div>

            {/* Discrepancy */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
              <div className="p-3.5 bg-amber-50 text-amber-600 rounded-xl"><AlertTriangle className="w-6 h-6" /></div>
              <div>
                <p className="text-sm text-gray-400 font-bold uppercase tracking-wide">One 100%, other not</p>
                <p className="text-2xl font-black text-amber-600 mt-0.5">{stats.discrepancy.toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* ── Regional Bar Chart (ALL regions, scrollable) ─────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Regional Average Performance Index</h3>
                <p className="text-sm text-gray-400 mt-0.5">All regions — Class 10 vs Class 12 average PI comparison</p>
              </div>
              <span className="text-sm text-gray-400 font-medium flex-shrink-0">{regionalChartData.length} regions</span>
            </div>
            <div className="overflow-x-auto">
              <div style={{ minWidth: chartMinWidth }}>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={regionalChartData} margin={{ top: 12, right: 16, left: -8, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="region"
                      tick={{ fontSize: 12, fill: '#374151' }}
                      tickLine={false}
                      angle={-35}
                      textAnchor="end"
                      height={80}
                      dy={16}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} domain={[50, 'auto']} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
                    <Legend wrapperStyle={{ fontSize: 13, paddingTop: 8 }} />
                    <Bar dataKey="Class 10 PI" fill="#F36B21" radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="Class 12 PI" fill="#9333EA" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* ── Deep Dive School Tables ─────────────────────────────────── */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

            {/* Tabs bar */}
            <div className="border-b border-gray-200 bg-gray-50/50 flex flex-wrap justify-between items-center px-4 py-1 gap-2">
              <nav className="flex flex-wrap gap-1">
                {TABS.map(t => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  // count badge
                  const cnt =
                    t.id === 'class10-100' ? stats.c10_100 :
                      t.id === 'class12-100' ? stats.c12_100 :
                        t.id === 'both-100' ? stats.both_100 :
                          t.id === 'neither-100' ? stats.neither_100 :
                            t.id === 'discrepancy' ? stats.discrepancy : null;
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setActiveTab(t.id); setSearch(''); setSorts([]); }}
                      className={
                        'flex items-center gap-2 px-4 py-3.5 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ' +
                        (isActive ? 'border-[#F36B21] text-[#F36B21]' : 'border-transparent text-gray-500 hover:text-gray-700')
                      }
                    >
                      <Icon className="w-4 h-4" />
                      {t.label}
                      {cnt !== null && (
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-orange-100 text-[#F36B21]' : 'bg-gray-100 text-gray-500'}`}>
                          {cnt}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Search in Table Bar */}
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search school / region / KV code..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21]"
                  />
                </div>

                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F36B21] text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors shadow-sm"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* PI Filter Config Panel */}
            {activeTab === 'pi-filter' && (
              <div className="bg-amber-50/40 border-b border-amber-100 p-4 flex flex-col md:flex-row items-start md:items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2 text-amber-800">
                  <Filter className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm font-semibold">PI Range Filter:</span>
                </div>

                {/* Class selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Show schools where</span>
                  <select
                    value={piClass}
                    onChange={e => setPiClass(e.target.value as '10' | '12' | 'both')}
                    className="px-2 py-1 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400 cursor-pointer"
                  >
                    <option value="10">Class 10</option>
                    <option value="12">Class 12</option>
                    <option value="both">Class 10 or 12 (either)</option>
                  </select>
                  <span className="text-sm text-gray-500">PI is between:</span>
                </div>

                {/* Min / Max inputs */}
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-500 font-medium">Min</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={piMin}
                    onChange={e => setPiMin(e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value))))}
                    className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-center focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21]"
                  />
                  <span className="text-gray-400 font-bold">—</span>
                  <label className="text-sm text-gray-500 font-medium">Max</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="100"
                    value={piMax}
                    onChange={e => setPiMax(e.target.value === '' ? '' : Math.max(0, Math.min(100, Number(e.target.value))))}
                    className="w-16 px-2 py-1.5 border border-gray-300 rounded-lg text-sm font-bold text-center focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-[#F36B21]"
                  />
                  <span className="text-sm text-gray-500">PI points</span>
                </div>

                <p className="text-sm text-gray-500 italic md:ml-auto">
                  PI range: <span className="font-bold text-gray-700">{piMinVal}–{piMaxVal}</span> &bull; {filteredDisplayData.length} schools matched
                </p>
              </div>
            )}

            {/* Active Sort Bar */}
            {sorts.length > 0 && (
              <div className="bg-orange-50/30 border-b border-orange-100 p-3 flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                  Active Sorts (Cascading):
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {sorts.map((s, idx) => {
                    const labelMap: Record<string, string> = {
                      schoolName: 'School Name',
                      region: 'Region',
                      class10Appeared: '10th Appeared',
                      class10Passed: '10th Passed',
                      class10PassPercentage: '10th Pass %',
                      class10PI: '10th PI',
                      class12Appeared: '12th Appeared',
                      class12Passed: '12th Passed',
                      class12PassPercentage: '12th Pass %',
                      class12PI: '12th PI',
                      gap: 'Gap',
                    };
                    return (
                      <span key={s.key} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-orange-100/60 text-[#F36B21] text-xs font-semibold border border-orange-200">
                        {idx + 1}. {labelMap[s.key] || s.key} {s.dir === 'asc' ? '▲' : '▼'}
                        <button
                          onClick={() => setSorts(prev => prev.filter(p => p.key !== s.key))}
                          className="hover:text-orange-800 transition-colors ml-1 font-bold text-sm leading-none"
                        >
                          &times;
                        </button>
                      </span>
                    );
                  })}
                </div>
                <button
                  onClick={() => setSorts([])}
                  className="text-xs text-gray-400 hover:text-[#F36B21] font-semibold transition-colors md:ml-auto"
                >
                  Clear All Sorts
                </button>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto max-h-[520px]">
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b border-gray-200 z-10">
                  <tr>
                    <th className="py-3.5 px-4 font-semibold text-gray-500 w-12">#</th>
                    {renderSortableHeader('School Name', 'schoolName')}
                    {renderSortableHeader('Region', 'region')}

                    {activeTab === 'class10-100' && (
                      <>
                        {renderSortableHeader('10th Appeared', 'class10Appeared', 'center')}
                        {renderSortableHeader('10th Passed', 'class10Passed', 'center')}
                        {renderSortableHeader('Pass Rate', 'class10PassPercentage', 'center', 'bg-green-50/50 text-green-700')}
                        {renderSortableHeader('PI', 'class10PI', 'center', 'text-orange-600')}
                      </>
                    )}
                    {activeTab === 'class12-100' && (
                      <>
                        {renderSortableHeader('12th Appeared', 'class12Appeared', 'center')}
                        {renderSortableHeader('12th Passed', 'class12Passed', 'center')}
                        {renderSortableHeader('Pass Rate', 'class12PassPercentage', 'center', 'bg-blue-50/50 text-blue-700')}
                        {renderSortableHeader('PI', 'class12PI', 'center', 'text-orange-600')}
                      </>
                    )}
                    {isBothTab && (
                      <>
                        {renderSortableHeader('10th Appeared', 'class10Appeared', 'center', 'bg-green-50/30 text-green-700')}
                        {renderSortableHeader('10th Pass%', 'class10PassPercentage', 'center', 'bg-green-50/30 text-green-700')}
                        {renderSortableHeader('10th PI', 'class10PI', 'center', 'bg-green-50/30 text-green-700')}
                        {renderSortableHeader('12th Appeared', 'class12Appeared', 'center', 'bg-purple-50/30 text-purple-700')}
                        {renderSortableHeader('12th Pass%', 'class12PassPercentage', 'center', 'bg-purple-50/30 text-purple-700')}
                        {renderSortableHeader('12th PI', 'class12PI', 'center', 'bg-purple-50/30 text-purple-700')}
                      </>
                    )}
                    {activeTab === 'discrepancy' && (
                      <>
                        {renderSortableHeader('10th Appeared', 'class10Appeared', 'center')}
                        {renderSortableHeader('10th Pass%', 'class10PassPercentage', 'center')}
                        {renderSortableHeader('12th Appeared', 'class12Appeared', 'center')}
                        {renderSortableHeader('12th Pass%', 'class12PassPercentage', 'center')}
                        {renderSortableHeader('Gap', 'gap', 'center', 'text-[#F36B21]')}
                      </>
                    )}
                    {activeTab === 'pi-filter' && (
                      <>
                        {renderSortableHeader('10th PI', 'class10PI', 'center')}
                        {renderSortableHeader('12th PI', 'class12PI', 'center')}
                        <th className="py-3.5 px-4 font-semibold text-red-600 text-center">Warning</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredDisplayData.map((item, idx) => (
                    <tr key={item.kvCode + idx} className="hover:bg-gray-50/80 transition-colors">
                      <td className="py-3 px-4 text-gray-400 font-medium">{idx + 1}</td>
                      <td className="py-3 px-4 font-semibold text-gray-800">
                        {item.schoolName}
                        <span className="text-sm text-gray-500 block font-medium">Code: {item.kvCode}</span>
                      </td>
                      <td className="py-3 px-4 text-gray-500 font-medium">{item.region}</td>

                      {activeTab === 'class10-100' && (
                        <>
                          <td className="py-3 px-4 text-center text-gray-700">{item.class10Appeared.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center text-gray-700">{item.class10Passed.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center font-black text-green-600 bg-green-50/20">{item.class10PassPercentage.toFixed(1)}%</td>
                          <td className="py-3 px-4 text-center font-bold text-gray-700">{item.class10PI.toFixed(2)}</td>
                        </>
                      )}
                      {activeTab === 'class12-100' && (
                        <>
                          <td className="py-3 px-4 text-center text-gray-700">{item.class12Appeared.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center text-gray-700">{item.class12Passed.toLocaleString()}</td>
                          <td className="py-3 px-4 text-center font-black text-blue-600 bg-blue-50/20">{item.class12PassPercentage.toFixed(1)}%</td>
                          <td className="py-3 px-4 text-center font-bold text-gray-700">{item.class12PI.toFixed(2)}</td>
                        </>
                      )}
                      {isBothTab && (
                        <>
                          <td className="py-3 px-4 text-center text-gray-700 bg-green-50/10">{item.class10Appeared > 0 ? item.class10Appeared.toLocaleString() : '—'}</td>
                          <td className="py-3 px-4 text-center bg-green-50/10">
                            <span className={`font-bold ${item.class10PassPercentage === 100 ? 'text-green-600' : 'text-gray-700'}`}>
                              {item.class10Appeared > 0 ? item.class10PassPercentage.toFixed(1) + '%' : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-semibold text-gray-700 bg-green-50/10">{item.class10Appeared > 0 ? item.class10PI.toFixed(2) : '—'}</td>
                          <td className="py-3 px-4 text-center text-gray-700 bg-purple-50/10">{item.class12Appeared > 0 ? item.class12Appeared.toLocaleString() : '—'}</td>
                          <td className="py-3 px-4 text-center bg-purple-50/10">
                            <span className={`font-bold ${item.class12PassPercentage === 100 ? 'text-emerald-600' : 'text-gray-700'}`}>
                              {item.class12Appeared > 0 ? item.class12PassPercentage.toFixed(1) + '%' : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-semibold text-gray-700 bg-purple-50/10">{item.class12Appeared > 0 ? item.class12PI.toFixed(2) : '—'}</td>
                        </>
                      )}
                      {activeTab === 'discrepancy' && (
                        <>
                          <td className="py-3 px-4 text-center text-gray-500">{item.class10Appeared ? item.class10Appeared.toLocaleString() : '—'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-0.5 rounded text-sm font-bold ${item.class10PassPercentage === 100 ? 'bg-green-100 text-green-700' : 'text-gray-700'}`}>
                              {item.class10Appeared ? item.class10PassPercentage.toFixed(1) + '%' : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-gray-500">{item.class12Appeared ? item.class12Appeared.toLocaleString() : '—'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-0.5 rounded text-sm font-bold ${item.class12PassPercentage === 100 ? 'bg-green-100 text-green-700' : 'text-gray-700'}`}>
                              {item.class12Appeared ? item.class12PassPercentage.toFixed(1) + '%' : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center font-black text-amber-600">
                            {Math.abs(item.class10PassPercentage - item.class12PassPercentage).toFixed(1)}%
                          </td>
                        </>
                      )}
                      {activeTab === 'pi-filter' && (
                        <>
                          <td className="py-3 px-4 text-center">
                            <span className={`${item.class10Appeared > 0 && item.class10PI >= piMinVal && item.class10PI <= piMaxVal
                                ? 'text-red-500 font-bold' : 'text-gray-700'
                              }`}>
                              {item.class10Appeared > 0 ? item.class10PI.toFixed(2) : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`${item.class12Appeared > 0 && item.class12PI >= piMinVal && item.class12PI <= piMaxVal
                                ? 'text-red-500 font-bold' : 'text-gray-700'
                              }`}>
                              {item.class12Appeared > 0 ? item.class12PI.toFixed(2) : '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            {item.class10Appeared > 0 && item.class10PI >= piMinVal && item.class10PI <= piMaxVal &&
                              item.class12Appeared > 0 && item.class12PI >= piMinVal && item.class12PI <= piMaxVal ? (
                              <span className="px-2.5 py-0.5 bg-red-100 text-red-700 rounded-full text-sm font-bold border border-red-200">Both in Range</span>
                            ) : item.class10Appeared > 0 && item.class10PI >= piMinVal && item.class10PI <= piMaxVal ? (
                              <span className="px-2.5 py-0.5 bg-orange-100 text-orange-700 rounded-full text-sm font-bold border border-orange-200">Class 10</span>
                            ) : (
                              <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 rounded-full text-sm font-bold border border-amber-200">Class 12</span>
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredDisplayData.length === 0 && (
                    <tr>
                      <td colSpan={12} className="py-12 text-center text-gray-400 font-medium">
                        No school records found matching your filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Row count footer */}
            <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/40 flex items-center justify-between">
              <span className="text-sm text-gray-400">{filteredDisplayData.length} schools shown</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
